import React, { useState } from 'react';
import { cn } from '../../lib/utils';
import { ChevronDown } from 'lucide-react';
import Analytics from '../../lib/analytics';

interface FAQItem {
  question: string;
  answer: string;
}

const faqData: FAQItem[] = [
  {
    question: 'What data sources can DataWyse connect to?',
    answer:
      'DataWyse connects seamlessly with popular business platforms across ERP, CRM, Inventory, and finance. We also support integration with in-house or industry-specific software.',
  },
  {
    question: 'How up-to-date will the data be?',
    answer:
      'By default we sync data every 24 hours. Sync frequency can be increased or customised to meet your requirements.',
  },
  {
    question: 'Do I need data-analysis experience to use DataWyse?',
    answer:
      'Absolutely not. DataWyse is built so that any business leader can use it right away with zero learning curve. Ask in plain English, and DataWyse will fetch the right data, analyze it, and return actionable, business-specific insights.',
  },
  {
    question: 'What if DataWyse hallucinates or returns incorrect information?',
    answer:
      'We have strict guardrails in place to prevent the LLM from altering the raw data. Core data operations and analytics are handled by our in-house algorithms. Every LLM-generated result goes through a two-step validation. This ensures the accuracy of both the data and the analysis. In short, the data you receive is not altered by the LLM, eliminating scope for any incorrect information.',
  },
];

interface AccordionItemProps {
  item: FAQItem;
  isOpen: boolean;
  onToggle: () => void;
  index: number;
}

const AccordionItem: React.FC<AccordionItemProps> = ({
  item,
  isOpen,
  onToggle,
  index,
}) => {
  const handleToggle = () => {
    // Track FAQ interaction
    Analytics.trackFAQ(isOpen ? 'faq_collapse' : 'faq_expand', {
      question_text: item.question,
      item_index: index,
    });
    onToggle();
  };

  return (
    <div
      className={cn(
        'rounded-2xl border bg-white/50 backdrop-blur-sm transition-all duration-300',
        isOpen
          ? 'border-blue-200/80 bg-white/80 shadow-lg'
          : 'border-slate-200/60 hover:border-slate-300/80 hover:bg-white/70 hover:shadow-md'
      )}
    >
      <button
        onClick={handleToggle}
        className="group flex w-full items-center justify-between rounded-2xl px-10 py-8 text-left focus:outline-none max-[599px]:px-6 max-[599px]:py-6"
      >
        <span className="pr-8 text-xl font-bold text-slate-900 transition-colors duration-200 group-hover:text-blue-600 max-[599px]:pr-4 max-[599px]:text-lg lg:text-2xl">
          {item.question}
        </span>
        <ChevronDown
          className={cn(
            'h-8 w-8 flex-shrink-0 text-slate-500 transition-all duration-300 group-hover:text-blue-600 max-[599px]:h-6 max-[599px]:w-6',
            isOpen && 'rotate-180'
          )}
        />
      </button>
      <div
        className={cn(
          'overflow-hidden transition-all duration-300 ease-in-out',
          isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <div className="px-10 pb-8 max-[599px]:px-6 max-[599px]:pb-6">
          <p className="text-lg leading-relaxed text-slate-600 max-[599px]:text-base max-[599px]:leading-relaxed lg:text-xl lg:leading-8">
            {item.answer}
          </p>
        </div>
      </div>
    </div>
  );
};

interface FAQSectionProps {
  className?: string;
}

export const FAQSection: React.FC<FAQSectionProps> = ({ className }) => {
  const [openItem, setOpenItem] = useState<number | null>(0); // First item open by default

  const toggleItem = (index: number) => {
    // If clicking on the already open item, close it; otherwise, open the clicked item
    setOpenItem(openItem === index ? null : index);
  };

  return (
    <section
      id="faq"
      className={cn(
        'relative overflow-hidden bg-violet-50 py-20 lg:py-32',
        className
      )}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-20 text-center">
          <h2 className="font-primary mb-6 text-5xl font-bold text-slate-900 max-[599px]:text-3xl lg:text-6xl">
            Frequently Asked Questions
          </h2>
        </div>

        {/* FAQ Container */}
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          {/* Decorative background elements */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 left-1/4 h-32 w-32 rounded-full bg-purple-100/30 blur-3xl"></div>
            <div className="absolute right-1/4 bottom-1/4 h-40 w-40 rounded-full bg-blue-100/30 blur-3xl"></div>
          </div>

          {/* FAQ Items */}
          <div className="relative z-10 space-y-6">
            {faqData.map((item, index) => (
              <AccordionItem
                key={index}
                item={item}
                isOpen={openItem === index}
                onToggle={() => toggleItem(index)}
                index={index}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
